import apiClient from './axios';

/**
 * Business storefronts awaiting PayIQ's decision.
 *
 * This queue is the reason a business cannot list itself on the consumer
 * marketplace unaided. KYB establishes that the company is real; it says
 * nothing about what a particular shop sells, at what price, or with what
 * claims on the page — and a shopper arriving from our own marketplace is
 * trusting that somebody here looked at exactly that.
 */

export type StorefrontReviewStatus =
  | 'draft'
  | 'in_review'
  | 'approved'
  | 'rejected';

export interface StorefrontProductPreview {
  id: string;
  name: string;
  price: number;
  imageUrl: string | null;
}

export interface StorefrontForReview {
  id: string;
  businessId: string | null;
  name: string;
  slug: string;
  /** Where the shop will live once its owner switches it on. */
  url: string;
  tagline: string | null;
  description: string | null;
  /** Null when the shop was created before shop-level categories existed. */
  category: string | null;
  contact: {
    email: string | null;
    phone: string | null;
    whatsapp: string | null;
    /** Formatted, or null when nothing was ever confirmed on the map step. */
    address: string | null;
  };
  branding: {
    logoUrl: string | null;
    coverUrl: string | null;
    accentColour: string | null;
  };
  productCount: number;
  /** First four only — the full catalogue is what the storefront URL is
   *  for; this is enough to judge what's being sold at a glance. */
  productPreview: StorefrontProductPreview[];
  reviewStatus: StorefrontReviewStatus;
  /** ISO. Null only for rows that never went through submission. */
  submittedAt: string | null;
}

export const storefrontsApi = {
  /** Defaults to the pending queue, oldest submission first. */
  async queue(status: StorefrontReviewStatus = 'in_review'): Promise<StorefrontForReview[]> {
    const res = await apiClient.get('/admin/storefronts', {
      params: { status },
    });
    return res.data.data;
  },

  /**
   * Clears the shop to be listed.
   *
   * Does **not** put it live — it grants the owner the right to switch it on,
   * so approving cannot open a shop somebody was not ready to open.
   */
  async approve(vendorId: string, note?: string): Promise<void> {
    await apiClient.post(`/admin/storefronts/${vendorId}/approve`,
      note ? { note } : {},
    );
  },

  /** The reason is shown to the seller verbatim, so it has to say what to
   *  fix — the server enforces a floor of ten characters for that reason. */
  async reject(vendorId: string, reason: string): Promise<void> {
    await apiClient.post(`/admin/storefronts/${vendorId}/reject`, { reason });
  },
};

export const REVIEW_STATUS_LABEL: Record<StorefrontReviewStatus, string> = {
  draft: 'Draft',
  in_review: 'Awaiting review',
  approved: 'Approved',
  rejected: 'Rejected',
};

/** How long a submission has been sitting. The queue is ordered oldest-first,
 *  so this is the number that says whether that ordering is keeping up. */
export function waitingFor(submittedAt: string | null): string {
  if (!submittedAt) return '—';

  const hours = Math.floor(
    (Date.now() - new Date(submittedAt).getTime()) / 3_600_000,
  );
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}
